import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CheckCircle2, CircleAlert, Search, Smartphone } from "lucide-react";
import Watermark from "@/components/app/Watermark";

export const MethodTick = ({ has, onClick, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId} title={has ? "Method added" : "No method yet"}
    className="inline-flex align-middle ml-2">
    {has ? <CheckCircle2 size={16} className="text-emerald-600" /> : <CircleAlert size={16} className="text-amber-500" />}
  </button>
);

const IngredientList = ({ ings }) => (
  <div className="prep-panel prep-ings rounded-xl bg-white border border-[#E4D6A8] p-4 md:max-h-[60vh] overflow-y-auto" data-testid="prep-ingredients">
    <div className="text-[10px] font-mono2 uppercase tracking-widest text-[#B4841F] mb-2">Ingredients · measure</div>
    {(ings || []).length === 0 && <div className="text-sm text-[#7a6349]">No ingredients listed</div>}
    <ul className="divide-y divide-[#F3E7C4]">
      {(ings || []).map((g, i) => (
        <li key={i} className="flex items-center justify-between gap-3 py-2.5 text-base" data-testid={`prep-ing-${i}`}>
          <span className="font-medium">{g.name}</span>
          <span className="font-mono2 font-bold text-[#3B2519] bg-[#FFF3CC] px-2 rounded-md whitespace-nowrap">{g.measure ?? (g.qty ? `${g.qty} ${g.qty_unit || ""}` : g.unit) ?? "—"}</span>
        </li>
      ))}
    </ul>
  </div>
);

const MethodSteps = ({ text }) => {
  const steps = (text || "").split("\n").map(s => s.trim()).filter(Boolean);
  if (!steps.length) return <div className="text-sm text-[#7a6349]">No preparation method added yet.</div>;
  return (
    <ol className="prep-steps space-y-2.5">
      {steps.map((s, i) => (
        <li key={i} className="flex gap-3 text-base leading-snug">
          <span className="shrink-0 w-7 h-7 rounded-full bg-[#3B2519] text-[#F5C242] text-sm font-bold flex items-center justify-center">{i + 1}</span>
          <span className="pt-0.5">{s.replace(/^(step\s*\d+\s*[:.)-]?|\d+\s*[.)-])\s*/i, "")}</span>
        </li>
      ))}
    </ol>
  );
};

export function PrepMethodDialog({ item, canEdit, email, onClose, onSaved }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setText(item?.method || ""); }, [item]);
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/prep-methods/${item.id}`, { method: text });
      toast.success("Method saved"); onSaved && onSaved(item.id, text); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="prep-sheet no-select bg-[#F5EFE0] border-[#E4D6A8] text-[#2b1a0d] w-[100vw] max-w-5xl overflow-y-auto overflow-x-hidden rounded-none md:rounded-2xl p-4 md:p-6" data-testid="prep-method-dialog"
        onContextMenu={e => e.preventDefault()}>
        {email && <Watermark email={email} contained />}
        <DialogHeader><DialogTitle className="font-display text-2xl md:text-3xl text-left pr-6">{item?.name}</DialogTitle></DialogHeader>
        <div className="prep-rotate-hint hidden items-center gap-2 text-xs text-[#7a6349] -mt-2" data-testid="prep-rotate-hint">
          <Smartphone size={14} className="rotate-90" /> Turn your phone sideways to see ingredients and method side by side
        </div>
        <div className="prep-grid grid md:grid-cols-[minmax(240px,2fr)_3fr] gap-4">
          <IngredientList ings={item?.ingredients} />
          <div className="prep-panel rounded-xl bg-white border border-[#E4D6A8] p-4 md:max-h-[60vh] overflow-y-auto" data-testid="prep-method-view">
            <div className="text-[10px] font-mono2 uppercase tracking-widest text-[#B4841F] mb-2">Preparation method</div>
            {canEdit ? (
              <Textarea data-testid="prep-method-input" value={text} onChange={e => setText(e.target.value)} rows={12}
                placeholder={"One step per line\nAdd 150ml milk to pitcher\nSteam to 65°C"} className="bg-[#FFFDF6] text-base" />
            ) : <MethodSteps text={item?.method} />}
          </div>
        </div>
        <DialogFooter className={canEdit ? "" : "prep-footer-view"}>
          <Button variant="ghost" onClick={onClose}>Close</Button>
          {canEdit && <Button data-testid="prep-method-save" onClick={save} disabled={busy}
            className="bg-[#3B2519] text-[#F5C242] hover:bg-black">{busy ? "Saving…" : "Save method"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Stat = ({ label, value, tone, testId, active, onClick }) => (
  <button onClick={onClick} data-testid={testId}
    className={`text-left rounded-2xl border p-4 bg-white transition-colors ${active ? "border-[#3B2519] ring-1 ring-[#3B2519]" : "border-[#E4D6A8] hover:bg-[#FFF8E7]"}`}>
    <div className="text-[10px] font-mono2 uppercase tracking-widest text-[#7a6349]">{label}</div>
    <div className={`font-display text-3xl mt-1 ${tone}`}>{value}</div>
  </button>
);

export default function PrepMethodTab({ canEdit, email }) {
  const [data, setData] = useState({ items: [], total: 0, with_method: 0, missing: 0 });
  const [filter, setFilter] = useState("all");
  const [cat, setCat] = useState("");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const load = () => api.get("/prep-methods").then(r => setData(r.data)).catch(e => toast.error(e.response?.data?.detail || "Failed to load"));
  useEffect(() => { load(); }, []);
  const cats = [...new Map(data.items.map(i => [i.category_id, i.category_name])).entries()];
  const rows = data.items.filter(i =>
    (filter === "all" || (filter === "done" ? i.has_method : !i.has_method)) &&
    (!cat || i.category_id === cat) &&
    (!q || i.name.toLowerCase().includes(q.toLowerCase())));
  return (
    <div data-testid="prep-method-tab">
      <h2 className="font-display text-3xl">Preparation Method</h2>
      <p className="text-sm text-[#7a6349] mt-1 mb-5">Every item's step-by-step method in one place. Green tick = method added.</p>
      <div className="grid grid-cols-3 gap-3 mb-5">
        <Stat label="Total items" value={data.total} tone="text-[#2b1a0d]" testId="prep-stat-total" active={filter === "all"} onClick={() => setFilter("all")} />
        <Stat label="Method added" value={data.with_method} tone="text-emerald-700" testId="prep-stat-done" active={filter === "done"} onClick={() => setFilter("done")} />
        <Stat label="Without method" value={data.missing} tone="text-amber-600" testId="prep-stat-missing" active={filter === "missing"} onClick={() => setFilter("missing")} />
      </div>
      <div className="flex gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[180px] max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 opacity-60" />
          <Input data-testid="prep-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Search item" className="pl-9 bg-white border-[#E4D6A8]" />
        </div>
        <select data-testid="prep-cat-filter" value={cat} onChange={e => setCat(e.target.value)}
          className="h-9 px-2 rounded-md border bg-white text-sm" style={{ borderColor: "#E4D6A8" }}>
          <option value="">All categories</option>
          {cats.map(([id, n]) => <option key={id} value={id}>{n}</option>)}
        </select>
      </div>
      <div className="rounded-2xl border border-[#E4D6A8] bg-white divide-y divide-[#F3E7C4]">
        {rows.map(i => (
          <button key={i.id} onClick={() => setOpen(i)} data-testid={`prep-row-${i.id}`}
            className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[#FFF8E7] transition-colors">
            {i.has_method ? <CheckCircle2 size={18} className="text-emerald-600 shrink-0" data-testid={`prep-tick-${i.id}`} />
              : <CircleAlert size={18} className="text-amber-500 shrink-0" data-testid={`prep-missing-${i.id}`} />}
            <div className="min-w-0 flex-1">
              <div className="font-medium truncate">{i.name}</div>
              <div className="text-xs text-[#7a6349] truncate">{i.category_name} · {i.ingredients.length} ingredient{i.ingredients.length === 1 ? "" : "s"}{i.has_method ? ` · ${i.method.split("\n")[0].slice(0, 80)}` : " · no method yet"}</div>
            </div>
            <span className="text-[10px] font-mono2 uppercase tracking-widest text-[#B4841F] shrink-0">{canEdit ? (i.has_method ? "Edit" : "Add") : "View"}</span>
          </button>
        ))}
        {rows.length === 0 && <div className="py-8 text-center text-sm text-[#7a6349]">No items here</div>}
      </div>
      <PrepMethodDialog item={open} canEdit={canEdit} email={email} onClose={() => setOpen(null)} onSaved={load} />
    </div>
  );
}
